import { FC } from 'react';
import { Box, Skeleton, Text } from '@hopskipdrive/react-components';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { Card } from '../../../../../shared/components/Card';
import { RiderInfoContainer } from '../../../../../shared/components/RiderInfoContainer';
import { colorTokens } from '../../../../../shared/components/variables';
import { ErrorBoundary } from '../../../../../shared/utils/errorReporting/ErrorBoundary';
import {
  RiderAdditionalInfo,
  RiderDetailsPresenter,
  RiderMainHeading,
  RiderSafetyBadges,
} from '../../../../components/RiderDetailsPresenter';
import { SkeletonRides, UpcomingRides } from '../../../../components/UpcomingRides';
import { useRiderDetailsQuery } from '../../../../hooks/useRiderDetailsQuery';
import { RemoveRider } from '../RemoveRider';
import { RiderContacts } from '../RiderContacts';
import { SkeletonContact } from '../RiderContacts/RiderContact';

import { Heading, InnerCard } from './RiderDetails.styles';

interface RiderDetailsProps {
  riderSlug: string;
  profileUrl: string;
  organizerName: string;
  isRemovedRider: boolean;
  isCarePartnerRide?: boolean;
}

export const RiderDetails: FC<RiderDetailsProps> = ({
  riderSlug,
  profileUrl,
  organizerName,
  isRemovedRider,
  isCarePartnerRide = false,
}) => {
  const { data: riderDetailsResult, isFetching } = useRiderDetailsQuery({ riderSlug: riderSlug });
  const riderDetails = riderDetailsResult?.data;

  return (
    <Card>
      <RiderMainHeading orgName={organizerName} slug={riderSlug}>
        {!isFetching && !isRemovedRider && !isCarePartnerRide && <RemoveRider riderSlug={riderSlug} />}
      </RiderMainHeading>
      <RiderDetailsPresenter
        riderSlug={riderSlug}
        profileUrl={profileUrl}
        isRemovedRider={isRemovedRider}
        riderDetails={riderDetails}
        isFetching={isFetching}
        isCarePartnerRide={isCarePartnerRide}
      />
      {(isFetching || !!riderDetails?.riderNotes) && (
        <InnerCard>
          <Box display="flex" flexDirection="row" alignItems="center">
            <DescriptionOutlinedIcon />
            <Text variant="h6">Rider Notes</Text>
          </Box>

          <Box maxHeight="166px" overflow="auto">
            {isFetching ? (
              <>
                <Skeleton variant="text" width={400} height={16} />
                <Skeleton variant="text" width={450} height={16} />
                <Skeleton variant="text" width={450} height={16} />
              </>
            ) : (
              <Text variant="body1" style={{ color: colorTokens.text.secondary, whiteSpace: 'pre-wrap' }}>
                {riderDetails.riderNotes}
              </Text>
            )}
          </Box>
        </InnerCard>
      )}
      <InnerCard>
        <ErrorBoundary FallbackComponent={SkeletonContact}>
          <RiderContacts riderSlug={riderSlug} />
        </ErrorBoundary>
      </InnerCard>
      <Box gap="4px" width="100%">
        <ErrorBoundary FallbackComponent={SkeletonRides}>
          <UpcomingRides userSlug={riderSlug} />
        </ErrorBoundary>
      </Box>
    </Card>
  );
};

export const RiderDetailsSkeleton = ({ organizerName, riderSlug }) => {
  return (
    <Card>
      <Heading>
        <RiderMainHeading orgName={organizerName} slug={riderSlug} />
        <Box display="flex" flexDirection="row">
          <Box display="flex" flexDirection="column" minWidth="310px" gap="12px">
            <Box>
              <Skeleton data-testid="main-heading-skeleton" variant="circular" width={92} height={92} />
              <Skeleton variant="text" width={135} height={32} />
              <Skeleton variant="text" width={130} height={24} />
            </Box>
            <Box>
              <RiderInfoContainer isFetching={true}>{''}</RiderInfoContainer>
              <RiderInfoContainer isFetching={true}>{''}</RiderInfoContainer>
            </Box>
            <RiderSafetyBadges isFetching={true} riderSlug="" safetyBadges={[]} />
            <Box display="flex" flexDirection="row" alignItems="center" gap="5px">
              <Skeleton width="189px" />
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" minWidth="310px">
            <RiderAdditionalInfo isFetching={true} label="">
              {''}
            </RiderAdditionalInfo>
            <RiderAdditionalInfo isFetching={true} label="">
              {''}
            </RiderAdditionalInfo>
            <RiderAdditionalInfo isFetching={true} label="">
              {''}
            </RiderAdditionalInfo>
            <RiderAdditionalInfo isFetching={true} label="">
              {''}
            </RiderAdditionalInfo>
          </Box>
        </Box>
      </Heading>
      <InnerCard>
        <Box display="flex" flexDirection="row" alignItems="center">
          <DescriptionOutlinedIcon />
          <Text variant="h6">Rider Notes</Text>
        </Box>
        <Box maxHeight="166px" overflow="auto">
          <Skeleton variant="text" width={400} height={16} />
          <Skeleton variant="text" width={450} height={16} />
          <Skeleton variant="text" width={450} height={16} />
        </Box>
      </InnerCard>
    </Card>
  );
};
